import api from '@/api';
import * as types from '@/store/types';
import eventHub from '@/eventHub';

import cookie from 'shared-lib/helpers/cookie';
import deferred from 'shared-lib/helpers/deferred';
import Message from 'shared-lib/helpers/message';
import { clearNavState } from '@/helpers/nav';

const state = {
    isReady: false,
    isLoaded: false,
    isLogged: false,
    token_type: null,
    expires_in: null,
    access_token: null,
    refresh_token: null,
    ready: deferred(),
};


const getters = {
    auth: state => state,
};


const actions = {
    initAuth ({ dispatch, commit }) {
        return dispatch('getToken')
            .then((tokenData) => dispatch('setTokenCookie', tokenData))
            .then((tokenData) => {
                if (!tokenData) {
                    dispatch('getConfigTheme')
                        .finally(() => commit(types.AUTH_READY));
                    return;
                }

                commit(types.AUTH_SUCCESS, tokenData);

                return dispatch('getInitialData')
                    .then(() => commit(types.AUTH_READY))
                    .catch((error) => {
                        commit(types.AUTH_READY);
                        Message.apiError({ message: error.message });
                    });

            });
    },

    login ({ dispatch, commit }, credentials) {
        clearNavState();
        dispatch('clearData');

        return api.login(credentials)
            .then((responseTokenData) => dispatch('setTokenCookie', responseTokenData))
            .then((tokenData) => {
                if (!tokenData) {
                    return;
                }

                commit(types.AUTH_SUCCESS, tokenData);
                commit(types.RESET_CURRENT_USER);

                return dispatch('getInitialData')
                    .then(() => commit(types.AUTH_READY))
                    .catch((error) => {
                        Message.apiError({ message: error.message });
                        throw error;
                    });
            });
    },

    loginWithSocial ({ dispatch, commit }, { provider, params }) {
        clearNavState();
        dispatch('clearData');

        return api.socialAuthCallback(provider, params)
            .then((responseTokenData) => dispatch('setTokenCookie', responseTokenData))
            .then((tokenData) => {
                if (!tokenData) {
                    return;
                }

                commit(types.AUTH_SUCCESS, tokenData);
                commit(types.RESET_CURRENT_USER);

                return dispatch('getInitialData')
                    .then(() => commit(types.AUTH_READY))
                    .catch((error) => {
                        Message.apiError({ message: error.message });
                        throw error;
                    });
            });
    },

    logout ({ dispatch, commit }) {
        api.logout();
        commit(types.AUTH_LOGOUT);
        commit(types.RESET_CURRENT_USER);

        return dispatch('clearData');
    },

    getToken () {
        const tokenCookie = cookie.get('token');
        return tokenCookie ? JSON.parse(tokenCookie) : null;
    },

    clearData () {
        cookie.remove('token', { path: '/', domain: window.location.hostname });
        localStorage.clear();
    },

    setTokenCookie (redux, tokenData) {
        if (tokenData) {
            tokenData = tokenData.data || tokenData;
            const currentDate = Date.now();
            const expiresAt = Number(currentDate) + Number(tokenData.expires_in) * 1000;

            let cookieOptions = {
                domain: window.location.hostname,
                path: '/',
                expires: new Date(expiresAt),
                secure: process.env.NODE_ENV === 'production',
                sameSite: process.env.NODE_ENV === 'production' ? 'None' : 'Lax',
            };

            cookie.set(
                'token',
                JSON.stringify(tokenData),
                cookieOptions
            );
        }

        return Promise.resolve(tokenData);
    },

    getInitialData ({ dispatch }) {
        return Promise.all([
            dispatch('getConfig'),
            dispatch('getCurrentUser'),
            dispatch('getDictionaries'),
            dispatch('getCustomDictionaries'),
        ]);
    },
};

const mutations = {
    [types.AUTH_SUCCESS] (state, tokenData) {
        Object.assign(state, tokenData.data || tokenData);
        state.isReady = true;
        state.isLogged = true;
        state.errors = [];

        eventHub.$emit('auth:success');
    },

    [types.AUTH_LOGOUT] (state) {
        Object.assign(state, {
            isReady: true,
            isLogged: false,
            token_type: null,
            expires_in: null,
            access_token: null,
            refresh_token: null,
        });
        state.errors = [];
    },

    [types.AUTH_READY] (state) {
        state.isLoaded = true;
        state.ready.resolve();
    },
};

export default {
    state,
    getters,
    actions,
    mutations,
};
